import { HTTP_INTERCEPTORS, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LoginService } from '@src/app/services/login/login.service';
import { LoginServiceConfig } from '@src/app/configs/login.service.config';

/**
 * Interceptor to add and get authorisation cookie on request and response
 */
@Injectable()
export class AccessInterceptor implements HttpInterceptor {
  public readonly accessTokenHeader: string;

  constructor(
    public config: LoginServiceConfig,
    private _injector: Injector,
    private _router: Router
  ) {
    this.accessTokenHeader = config.accessTokenHeader;
  }

  /**
   * Overridden intercept method
   *
   */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Fetch the JWT that is possibly in memory
    // we use the injector to avoid a circular dependency between the HttpClient and the HttpInterceptors
    const loginService = this._injector.get(LoginService);
    const jwt = loginService.getJwt();

    const modifiedReq = jwt ? AccessInterceptor.requestWithToken(request, jwt) : request;

    // Make the request. If an error is returned then try to get a new access token and retry
    return next.handle(modifiedReq).pipe(
      concatMap((evt) => {
        if (AccessInterceptor.isUnauthorized(evt) && !loginService.retailerKey) {
          return this._retry$(next, modifiedReq);
        } else {
          return of(evt);
        }
      })
    );
  }

  /**
   * Checks a response to see whether the user was authorized to make the request or not.
   *
   */
  private static isUnauthorized(evt: any): boolean {
    return evt instanceof HttpResponse && evt.body && evt.body.errors && evt.body.errors.length > 0;
  }

  /** Returns clone of given request to which the given token is added as authorization header bearer */
  private static requestWithToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    // Add the JWT to the request header

    // Set the jwt in the cookie header. The cookie name Junglegymcms_ACCESS_SESSION
    // has to match the configuration in the backend (application.conf).
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return request.clone({
      headers,
      withCredentials: true,
    });
  }

  /**
   * Refreshes access token and retries request with refreshed token.
   * On failure, forces a logout.
   */
  private _retry$(next: HttpHandler, request: HttpRequest<any>): Observable<any> {
    const loginService = this._injector.get(LoginService);
    return loginService.refreshAccessToken$()
      .pipe(
        concatMap((refreshSuccessful) => {
          if (refreshSuccessful) {
            const jwt = loginService.getJwt();
            const newreq = jwt ? AccessInterceptor.requestWithToken(request, jwt) : request;
            return next.handle(newreq);
          } else {
            return loginService.logout$(false).pipe(
              map(_ => this._router.navigateByUrl('/'))
            );
          }
        })
      );
  }
}

export const accessInterceptorProvider = {
  provide: HTTP_INTERCEPTORS, useClass: AccessInterceptor, multi: true,
};
